import { cn } from "@/lib/utils"

interface StorageUsageBarProps {
  totalBytes: number
  usedBytes: number
  backupsBytes: number
  attachmentsBytes: number
  templatesBytes: number
  className?: string
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`
}

export function StorageUsageBar({
  totalBytes,
  usedBytes,
  backupsBytes,
  attachmentsBytes,
  templatesBytes,
  className
}: StorageUsageBarProps) {
  const isCloud = totalBytes <= 0
  
  // Calculate percentages
  // If cloud, we use usedBytes as the "total" for proportional rendering of the segments
  const denominator = isCloud ? usedBytes : totalBytes
  
  const backupsPct = denominator > 0 ? (backupsBytes / denominator) * 100 : 0
  const attachmentsPct = denominator > 0 ? (attachmentsBytes / denominator) * 100 : 0
  const templatesPct = denominator > 0 ? (templatesBytes / denominator) * 100 : 0
  const otherUsedPct = denominator > 0 ? ((usedBytes - backupsBytes - attachmentsBytes - templatesBytes) / denominator) * 100 : 0
  
  return (
    <div className={cn("space-y-3", className)}>
      {/* The Bar */}
      <div className="relative h-2 w-full overflow-hidden rounded-full bg-secondary flex">
        {/* Backups Segment (Blue) */}
        <div 
          className="h-full bg-blue-500 transition-all duration-500 ease-in-out" 
          style={{ width: `${backupsPct}%` }}
          title={`Backups: ${formatBytes(backupsBytes)}`}
        />
        {/* Attachments Segment (Emerald) */}
        <div 
          className="h-full bg-emerald-500 transition-all duration-500 ease-in-out border-l border-white/10" 
          style={{ width: `${attachmentsPct}%` }}
          title={`Attachments: ${formatBytes(attachmentsBytes)}`}
        />
        {/* Templates Segment (Amber) */}
        <div 
          className="h-full bg-amber-500 transition-all duration-500 ease-in-out border-l border-white/10" 
          style={{ width: `${templatesPct}%` }}
          title={`Templates: ${formatBytes(templatesBytes)}`}
        />
        {/* Other Used (Gray/Amber) - rarely needed but for completeness */}
        <div 
          className="h-full bg-slate-400 transition-all duration-500 ease-in-out border-l border-white/10" 
          style={{ width: `${otherUsedPct}%` }}
          title={`Other: ${formatBytes(usedBytes - backupsBytes - attachmentsBytes - templatesBytes)}`}
        />
      </div>

      {/* The Legend */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[10px] text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <div className="h-2 w-2 rounded-full bg-blue-500" />
          <span>Backups: <span className="font-medium text-foreground">{formatBytes(backupsBytes)}</span></span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="h-2 w-2 rounded-full bg-emerald-500" />
          <span>Attachments: <span className="font-medium text-foreground">{formatBytes(attachmentsBytes)}</span></span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="h-2 w-2 rounded-full bg-amber-500" />
          <span>Templates: <span className="font-medium text-foreground">{formatBytes(templatesBytes)}</span></span>
        </div>
        {!isCloud && (
          <div className="flex items-center gap-1.5">
            <div className="h-2 w-2 rounded-full bg-secondary" />
            <span>Free: <span className="font-medium text-foreground">{formatBytes(totalBytes - usedBytes)}</span></span>
          </div>
        )}
      </div>
    </div>
  )
}
